/**
 * ColumnSettingsModal - modal for editing column settings (rename, color, WIP limit, delete)
 */

import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBoardStore } from '@stores/boardStore';
import { toast } from '@components/ui/Toast';
import type { BoardColumn } from '@services/api';

interface ColumnSettingsModalProps {
  column: BoardColumn | null;
  boardId: string;
  taskCount: number;
  open: boolean;
  onClose: () => void;
}

const colorOptions = [
  { value: '#6B7280', label: 'Gray' },
  { value: '#EF4444', label: 'Red' },
  { value: '#F97316', label: 'Orange' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#22C55E', label: 'Green' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#A855F7', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
];

export default function ColumnSettingsModal({
  column,
  boardId,
  taskCount,
  open,
  onClose,
}: ColumnSettingsModalProps) {
  const { t } = useTranslation();
  const { updateColumn, deleteColumn } = useBoardStore();

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('#6B7280');
  const [wipLimit, setWipLimit] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset form when column changes
  useEffect(() => {
    if (column) {
      setName(column.name);
      setColor(column.color || '#6B7280');
      setWipLimit(column.wipLimit?.toString() || '');
      setShowDeleteConfirm(false);
    }
  }, [column]);

  const handleSave = async () => {
    if (!column || !name.trim()) return;

    setIsSaving(true);
    try {
      const updates: { name?: string; color?: string; wipLimit?: number } = {};

      if (name.trim() !== column.name) {
        updates.name = name.trim();
      }

      if (color !== column.color) {
        updates.color = color;
      }

      const newWipLimit = wipLimit ? parseInt(wipLimit, 10) : 0;
      const currentWipLimit = column.wipLimit ?? 0;
      // Only include wipLimit if it changed
      if (newWipLimit !== currentWipLimit) {
        // Pass the number value; 0 will be handled by the API as "no limit"
        updates.wipLimit = newWipLimit;
      }

      if (Object.keys(updates).length > 0) {
        await updateColumn(boardId, column.id, updates);
        toast.success(t('board.columnUpdated'));
      }

      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedUpdateColumn');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!column) return;

    if (taskCount > 0) {
      toast.error(t('board.deleteColumnHasTasks', { count: taskCount }));
      return;
    }

    setIsSaving(true);
    try {
      await deleteColumn(boardId, column.id);
      toast.success(t('board.columnDeleted'));
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedDeleteColumn');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!column) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('board.columnSettings')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded p-1 transition-colors',
                  'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                  'dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="column-name"
                className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('common.name', 'Name')}
              </label>
              <input
                id="column-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={clsx(
                  'w-full rounded-md border px-3 py-2',
                  'border-neutral-300 bg-white text-neutral-900',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                )}
                placeholder={t('board.columnNamePlaceholder')}
              />
            </div>

            {/* Color */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('board.colorLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setColor(opt.value)}
                    className={clsx(
                      'h-8 w-8 rounded-full transition-transform',
                      'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2',
                      color === opt.value && 'ring-2 ring-primary-500 ring-offset-2'
                    )}
                    style={{ backgroundColor: opt.value }}
                    title={opt.label}
                  />
                ))}
              </div>
            </div>

            {/* WIP Limit */}
            <div>
              <label
                htmlFor="wip-limit"
                className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('board.wipLimitLabel')}
              </label>
              <input
                id="wip-limit"
                type="number"
                min="0"
                value={wipLimit}
                onChange={(e) => setWipLimit(e.target.value)}
                className={clsx(
                  'w-full rounded-md border px-3 py-2',
                  'border-neutral-300 bg-white text-neutral-900',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                )}
                placeholder={t('board.wipLimitPlaceholder')}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {t('board.wipLimitHelp')}
              </p>
            </div>
          </div>

          {/* Delete section */}
          <div className="mt-6 border-t border-neutral-200 pt-4 dark:border-neutral-700">
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className={clsx(
                  'flex items-center gap-2 text-sm',
                  'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                )}
              >
                <Trash2 className="h-4 w-4" />
                {t('board.deleteColumn')}
              </button>
            ) : (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('board.deleteColumnConfirm')}
                    </p>
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      {taskCount > 0
                        ? t('board.deleteColumnHasTasks', { count: taskCount })
                        : t('board.deleteColumnCannotUndo')}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={taskCount > 0 || isSaving}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-red-600 text-white hover:bg-red-700',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      >
                        {t('common.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-neutral-200 text-neutral-700 hover:bg-neutral-300',
                          'dark:bg-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-500'
                        )}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                'dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isSaving ? t('common.saving') : t('common.saveChanges')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
